import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

const LandingPage = lazy(() => import('./landing'));
const PricingPage = lazy(() => import('./pricing'));
const AppLayout = lazy(() => import('./_layout'));
const LoginPage = lazy(() => import('./auth/login'));
const SignupPage = lazy(() => import('./auth/signup'));
const AcceptInvitePage = lazy(() => import('./auth/accept-invite'));
const DashboardPage = lazy(() => import('./app/dashboard'));
const UsersSettingsPage = lazy(() => import('./app/settings/users'));

const AcervoIndex = lazy(() => import('./app/acervo/index'));
const AcervoAdicionar = lazy(() => import('./app/acervo/adicionar'));
const AcervoBuscar = lazy(() => import('./app/acervo/buscar'));

const CatedraIndex = lazy(() => import('./app/catedra/index'));
const CatedraDetail = lazy(() => import('./app/catedra/detail'));
const CatedraLesson = lazy(() => import('./app/catedra/lesson'));

const AulaVivaIndex = lazy(() => import('./app/aula-viva/index'));

const CrivoIndex = lazy(() => import('./app/crivo/index'));
const CrivoDetail = lazy(() => import('./app/crivo/detail'));
const CrivoResponder = lazy(() => import('./app/crivo/responder'));
const CrivoSubmission = lazy(() => import('./app/crivo/submission'));

const ObservatorioIndex = lazy(() => import('./app/observatorio/index'));
const CuradorIndex = lazy(() => import('./app/curador/index'));
const PretorioIndex = lazy(() => import('./app/pretorio/index'));
const ConcilioIndex = lazy(() => import('./app/concilio/index'));

const ScriptoriumIndex = lazy(() => import('./app/scriptorium/index'));
const ScriptoriumEditor = lazy(() => import('./app/scriptorium/editor'));

const AgoraIndex = lazy(() => import('./app/agora/index'));
const AgoraDetail = lazy(() => import('./app/agora/detail'));

const JurisprudenciaIndex = lazy(() => import('./app/jurisprudencia/index'));

function PageFallback() {
  return (
    <div className="min-h-screen grid place-items-center">
      <p className="font-mono text-xs text-fg-muted">/ NOUS · carregando…</p>
    </div>
  );
}

function withSuspense(node: React.ReactNode) {
  return <Suspense fallback={<PageFallback />}>{node}</Suspense>;
}

/**
 * Router NOUS.
 * Regra v7: rotas estáticas DEVEM vir antes das dinâmicas.
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/app/dashboard" replace />,
  },
  // Páginas públicas (não-entry — para uso futuro / divulgação direta).
  // Não usar como root enquanto a prioridade for a plataforma operacional.
  {
    path: '/landing',
    element: withSuspense(<LandingPage />),
  },
  {
    path: '/pricing',
    element: withSuspense(<PricingPage />),
  },
  {
    path: '/auth/login',
    element: withSuspense(<LoginPage />),
  },
  {
    path: '/auth/signup',
    element: withSuspense(<SignupPage />),
  },
  {
    path: '/auth/accept-invite',
    element: withSuspense(<AcceptInvitePage />),
  },
  {
    path: '/app',
    element: withSuspense(<AppLayout />),
    children: [
      { index: true, element: <Navigate to="/app/dashboard" replace /> },
      { path: 'dashboard', element: withSuspense(<DashboardPage />) },
      { path: 'settings/users', element: withSuspense(<UsersSettingsPage />) },

      // Acervo (estáticas antes da dinâmica :id)
      { path: 'acervo', element: withSuspense(<AcervoIndex />) },
      { path: 'acervo/adicionar', element: withSuspense(<AcervoAdicionar />) },
      { path: 'acervo/buscar', element: withSuspense(<AcervoBuscar />) },

      // Cátedra
      { path: 'catedra', element: withSuspense(<CatedraIndex />) },
      { path: 'catedra/aula/:id', element: withSuspense(<CatedraLesson />) },
      { path: 'catedra/:id', element: withSuspense(<CatedraDetail />) },

      // Aula Viva (Sócrates)
      { path: 'aula-viva', element: withSuspense(<AulaVivaIndex />) },

      // Crivo (avaliações)
      { path: 'crivo', element: withSuspense(<CrivoIndex />) },
      { path: 'crivo/submission/:id', element: withSuspense(<CrivoSubmission />) },
      { path: 'crivo/:id/responder', element: withSuspense(<CrivoResponder />) },
      { path: 'crivo/:id', element: withSuspense(<CrivoDetail />) },

      // Observatório (analytics)
      { path: 'observatorio', element: withSuspense(<ObservatorioIndex />) },

      // Curador (research lines + alerts)
      { path: 'curador', element: withSuspense(<CuradorIndex />) },

      // Pretório (gestão de turma)
      { path: 'pretorio', element: withSuspense(<PretorioIndex />) },

      // Concílio (governança · LGPD)
      { path: 'concilio', element: withSuspense(<ConcilioIndex />) },

      // Scriptorium (Escriba)
      { path: 'scriptorium', element: withSuspense(<ScriptoriumIndex />) },
      { path: 'scriptorium/:id', element: withSuspense(<ScriptoriumEditor />) },

      // Ágora (discussões)
      { path: 'agora', element: withSuspense(<AgoraIndex />) },
      { path: 'agora/:id', element: withSuspense(<AgoraDetail />) },

      // Jurisprudência (vertical Direito)
      { path: 'jurisprudencia', element: withSuspense(<JurisprudenciaIndex />) },
    ],
  },
  {
    path: '*',
    element: (
      <div className="min-h-screen grid place-items-center px-6 text-center">
        <div>
          <p className="mg-eyebrow mb-3">/ 404</p>
          <h1 className="mg-h1 text-3xl mb-2">Rota não encontrada</h1>
          <p className="text-fg-muted">Volte ao painel para continuar.</p>
        </div>
      </div>
    ),
  },
]);
